import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';
import ModernMessageBubble from '../messaging/ModernMessageBubble';
import TimestampSeparator from '../messaging/TimestampSeparator';
import EmojiPicker from '../messaging/EmojiPicker';
import { groupMessages, organizeMessageThreads, flattenMessageThreads } from '../../utils/messageGrouping';

// Function to fetch proper item title
const fetchItemTitle = async (itemId, itemType) => {
  try {
    const docRef = doc(db, itemType === 'assignment' ? 'assignments' : 'class_management', itemId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.assignment_title || data.class_title || data.class_name || 'Untitled';
    }
    return 'Untitled';
  } catch (error) {
    console.error('Error fetching item title:', error);
    return 'Untitled';
  }
};

const CommentSection = ({ itemId, itemType, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { user, profile } = useAuth();

  // Load comments from Firestore
  useEffect(() => {
    if (!itemId || !itemType) return;

    const q = query(
      collection(db, 'comments'),
      where('item_id', '==', itemId),
      where('item_type', '==', itemType),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));
      setComments(commentsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [itemId, itemType]);

  // Get unique participants from existing comments
  const getConversationParticipants = () => {
    const participants = new Set();
    
    // Add all existing comment authors
    comments.forEach(comment => {
      if (comment.author_id) {
        participants.add(comment.author_id);
      }
    });
    
    // Always include current user
    participants.add(user.uid);
    
    return Array.from(participants).filter(id => id !== user.uid); // Exclude self from mentions
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const commentContent = newComment.trim();
    const tempId = `temp-${Date.now()}`;
    const mentions = getConversationParticipants();

    // OPTIMISTIC UPDATE: Show comment immediately with "sending" status
    const optimisticComment = {
      id: tempId,
      item_id: itemId,
      item_type: itemType,
      content: commentContent,
      author_id: user.uid,
      author_name: profile?.full_name || user.email,
      author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : profile?.is_client ? 'client' : 'user',
      created_at: new Date(),
      parent_id: null,
      read_by: [user.uid],
      mentions: mentions,
      reactions: {},
      edited: false,
      pinned: false,
      _optimistic: true,
      _status: 'sending'
    };

    // Add optimistic comment immediately
    setComments(prev => [optimisticComment, ...prev]);
    setNewComment('');
    setSubmitting(false);

    // Send to Firebase in background
    try {
      const itemTitle = await fetchItemTitle(itemId, itemType);
      
      await addDoc(collection(db, 'comments'), {
        item_id: itemId,
        item_type: itemType,
        item_title: itemTitle,
        content: commentContent,
        author_id: user.uid,
        author_name: profile?.full_name || user.email,
        author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : profile?.is_client ? 'client' : 'user',
        created_at: serverTimestamp(),
        parent_id: null,
        read_by: [user.uid],
        mentions: mentions,
        reactions: {},
        edited: false,
        pinned: false
      });
      
      // Update optimistic comment status to "sent"
      setComments(prev => prev.map(c => 
        c.id === tempId ? { ...c, _status: 'sent' } : c
      ));
      onCommentAdded && onCommentAdded();
      
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      
      // Mark optimistic comment as failed
      setComments(prev => prev.map(c => 
        c.id === tempId ? { ...c, _status: 'failed' } : c
      ));
    }
  };

  const handleSubmitReply = async (e, parentId) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      const mentions = getConversationParticipants();
      
      // Get item title for proper conversation naming
      const itemTitle = await fetchItemTitle(itemId, itemType);
      
      await addDoc(collection(db, 'comments'), {
        item_id: itemId,
        item_type: itemType,
        item_title: itemTitle, // Include proper title for conversations
        content: replyText.trim(),
        author_id: user.uid,
        author_name: profile?.full_name || user.email,
        author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : profile?.is_client ? 'client' : 'user',
        created_at: serverTimestamp(),
        parent_id: parentId,
        read_by: [user.uid], // Author has already read their own comment
        mentions: mentions, // Include all conversation participants
        reactions: {}, // Initialize reactions object
        edited: false,
        pinned: false
      });
      
      setReplyText('');
      setReplyingTo(null);
      toast.success('Reply added successfully');
      onCommentAdded && onCommentAdded();
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };


  // Organize comments into threads
  // Add emoji to message
  const handleEmojiSelect = (emoji) => {
    setNewComment(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Process comments with modern grouping and threading
  const processedComments = useMemo(() => {
    if (!comments || comments.length === 0) return [];
    
    // First organize threads
    const threadedComments = organizeMessageThreads(comments);
    
    // Then flatten for display with thread context
    const flatComments = flattenMessageThreads(threadedComments);
    
    // Finally group by time and sender
    return groupMessages(flatComments);
  }, [comments]);

  // Handle swipe to reply
  const handleSwipeReply = (comment) => {
    setReplyingTo(comment.id);
    // Add haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  // Handle context menu
  const handleContextMenu = (comment, position) => {
    console.log('Context menu for comment:', comment.id, 'at position:', position);
  };


  if (loading) {
    return (
      <div className="ios-card-secondary" style={{padding: 'var(--ios-space-5)', borderRadius: 'var(--ios-radius-xl)'}}>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-500/15 rounded-lg flex items-center justify-center animate-pulse" style={{borderRadius: 'var(--ios-radius-lg)'}}>
            <Icon name="MessageCircle" size={16} className="text-blue-400" />
          </div>
          <span className="ios-text-secondary" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>Loading comments...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ios-card-secondary space-y-4" style={{padding: 'var(--ios-space-5)', borderRadius: 'var(--ios-radius-xl)'}}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-500/15 rounded-lg flex items-center justify-center flex-shrink-0" style={{borderRadius: 'var(--ios-radius-lg)'}}>
            <Icon name="MessageCircle" size={16} className="text-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="ios-text-primary font-semibold text-base" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-semibold)'}}>Questions & Updates</h3>
            <p className="ios-text-secondary text-sm mt-0.5" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>Need help? Ask us anything!</p>
          </div>
        </div>
        <div className="ios-card-tertiary px-2.5 py-1.5 rounded-full flex-shrink-0" style={{borderRadius: 'var(--ios-radius-full)'}}>
          <span className="ios-text-secondary text-xs font-medium" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-medium)'}}>
            {comments.length}
          </span>
        </div>
      </div>

      {/* Comment Input */}
      <form onSubmit={handleSubmitComment} className="space-y-3">
        <div className="relative">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Ask a question or request an update..."
            className="w-full ios-card-tertiary border-none ios-text-primary text-sm placeholder-white/40 focus:ring-2 focus:ring-blue-400/30 resize-none transition-all"
            style={{
              padding: 'var(--ios-space-3) var(--ios-space-4)',
              borderRadius: 'var(--ios-radius-xl)',
              fontFamily: 'var(--ios-font-system)',
              transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
            }}
            rows={3}
            maxLength={500}
          />
          <div className="absolute bottom-3 right-4 ios-text-tertiary text-xs" style={{fontFamily: 'var(--ios-font-system)'}}>
            {newComment.length}/500
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Emoji Picker Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="ios-card-tertiary p-2.5 hover:bg-white/20 ios-text-secondary hover:text-white transition-all"
                style={{
                  borderRadius: 'var(--ios-radius-lg)',
                  transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
                }}
              >
                <Icon name="Smile" size={16} />
              </button>
              
              <EmojiPicker 
                isOpen={showEmojiPicker}
                onClose={() => setShowEmojiPicker(false)}
                onEmojiSelect={handleEmojiSelect}
              />
            </div>
            
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2.5 text-white font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{
                background: 'var(--ios-blue)',
                borderRadius: 'var(--ios-radius-lg)',
                fontFamily: 'var(--ios-font-system)',
                fontWeight: 'var(--ios-weight-semibold)',
                transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
              }}
            >
              {submitting ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span className="hidden sm:inline">Adding...</span>
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} />
                  <span className="hidden sm:inline">Add Comment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Comments List */}
      <div className="space-y-2 max-h-80 sm:max-h-96 overflow-y-auto ios-scrollbar" style={{scrollbarWidth: 'thin'}}>
        <AnimatePresence mode="popLayout">
          {comments.length === 0 ? (
            <div className="text-center py-8 ios-card-tertiary rounded-lg" style={{padding: 'var(--ios-space-6)', borderRadius: 'var(--ios-radius-lg)'}}>
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-3" style={{borderRadius: 'var(--ios-radius-full)'}}>
                <Icon name="MessageCircle" size={24} className="text-blue-400/50" />
              </div>
              <p className="ios-text-primary font-medium text-sm" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-medium)'}}>No comments yet</p>
              <p className="ios-text-secondary text-xs mt-1" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>Be the first to add a comment!</p>
            </div>
          ) : (
            processedComments.map((item, index) => {
              if (item.type === 'timestamp') {
                return (
                  <TimestampSeparator
                    key={item.id}
                    timestamp={item.content}
                  />
                );
              }

              return (
                <ModernMessageBubble
                  key={item.id}
                  message={item}
                  previousMessage={processedComments[index - 1]?.type === 'message' ? processedComments[index - 1] : null}
                  nextMessage={processedComments[index + 1]?.type === 'message' ? processedComments[index + 1] : null}
                  currentUser={user}
                  onReply={(comment) => setReplyingTo(comment.id)}
                  onSwipeReply={handleSwipeReply}
                  onContextMenu={handleContextMenu}
                  isLastInGroup={item.isLastInGroup}
                  showAvatar={item.showAvatar}
                  showTimestamp={item.showTimestamp}
                />
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Reply Indicator */}
      {replyingTo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="px-4 py-3 bg-white/5 border-t border-white/10 backdrop-blur-sm rounded-lg mt-4"
        >
          {(() => {
            const replyComment = comments.find(c => c.id === replyingTo);
            return (
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="Reply" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-primary text-sm font-medium">
                      Replying to {replyComment?.author_name}
                    </span>
                  </div>
                  {replyComment && (
                    <div className="bg-white/10 border-l-3 border-primary/50 rounded-lg p-2 ml-5">
                      <p className="text-white/70 text-sm line-clamp-2">
                        {replyComment.content}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                >
                  <Icon name="X" size={16} className="text-white/60" />
                </button>
              </div>
            );
          })()}
        </motion.div>
      )}

    </div>
  );
};

export default CommentSection;